import React, { useMemo, useRef, useEffect } from 'react';
import Map, { Marker, Source, Layer } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import ngeohash from 'ngeohash';
import { API_CONFIG } from '../config';
import styles from './GeohashPreviewMap.module.css';

function geohashesToFeatureCollection(geohashes) {
  const features = [];
  for (const raw of geohashes || []) {
    const gh = String(raw).trim();
    if (!gh) continue;
    try {
      const bbox = ngeohash.decode_bbox(gh);
      const [minLat, minLon, maxLat, maxLon] = bbox;
      features.push({
        type: 'Feature',
        properties: { geohash: gh },
        geometry: {
          type: 'Polygon',
          coordinates: [
            [
              [minLon, minLat],
              [maxLon, minLat],
              [maxLon, maxLat],
              [minLon, maxLat],
              [minLon, minLat],
            ],
          ],
        },
      });
    } catch {
      // invalid prefix
    }
  }
  return { type: 'FeatureCollection', features };
}

/**
 * Mapbox preview of advertising geohash prefix cells + optional reference point.
 * @param {{ geohashes: string[], adCenter?: { lat: number, lon: number } | null, height?: number }} props
 */
function GeohashPreviewMap({ geohashes = [], adCenter = null, height = 280 }) {
  const mapRef = useRef(null);
  const token = API_CONFIG.mapboxToken;

  const geojson = useMemo(() => geohashesToFeatureCollection(geohashes), [geohashes]);

  const parsedCenter = useMemo(() => {
    if (!adCenter) return null;
    const lat = Number(adCenter.lat);
    const lon = Number(adCenter.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    return { lat, lon };
  }, [adCenter]);

  const firstGeohashCenter = (() => {
    for (const raw of geohashes) {
      const gh = String(raw).trim();
      if (!gh) continue;
      try {
        const [minLat, minLon, maxLat, maxLon] = ngeohash.decode_bbox(gh);
        return { lat: (minLat + maxLat) / 2, lon: (minLon + maxLon) / 2 };
      } catch {
        // invalid prefix
      }
    }
    return null;
  })();

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !token) return;

    const applyView = () => {
      if (firstGeohashCenter) {
        map.easeTo({
          center: [firstGeohashCenter.lon, firstGeohashCenter.lat],
          zoom: 3,
          duration: 300,
        });
      } else if (parsedCenter) {
        map.easeTo({
          center: [parsedCenter.lon, parsedCenter.lat],
          zoom: 9,
          duration: 300,
        });
      }
    };

    if (!map.isStyleLoaded()) {
      map.once('load', applyView);
      return undefined;
    }
    applyView();
    return undefined;
  }, [firstGeohashCenter, parsedCenter, token]);

  if (!token) {
    return (
      <div className={styles.error}>
        Set <code>REACT_APP_MAPBOX_TOKEN</code> in <code>.env.local</code> to preview geohash zones on the map.
      </div>
    );
  }

  const hasCells = geojson.features.length > 0;
  const showMap = hasCells || parsedCenter;
  const initialLon = firstGeohashCenter?.lon ?? parsedCenter?.lon ?? -98;
  const initialLat = firstGeohashCenter?.lat ?? parsedCenter?.lat ?? 39;
  const initialZoom = firstGeohashCenter ? 3 : showMap ? 6 : 2;

  return (
    <div className={styles.wrap}>
      <p className={styles.caption}>Coverage preview (geohash cells)</p>
      {!showMap ? (
        <div className={styles.error}>
          Enter valid geohash prefixes or lat/lon to see the map.
        </div>
      ) : (
        <div className={styles.map} style={{ height }}>
          <Map
            ref={mapRef}
            mapboxAccessToken={token}
            initialViewState={{
              longitude: initialLon,
              latitude: initialLat,
              zoom: initialZoom,
            }}
            style={{ width: '100%', height: '100%' }}
            mapStyle="mapbox://styles/mapbox/streets-v12"
            scrollZoom
          >
            {hasCells && (
              <Source id="geohash-ad-preview" type="geojson" data={geojson}>
                <Layer
                  id="geohash-ad-fill"
                  type="fill"
                  paint={{ 'fill-color': '#1976d2', 'fill-opacity': 0.25 }}
                />
                <Layer
                  id="geohash-ad-line"
                  type="line"
                  paint={{ 'line-color': '#0d47a1', 'line-width': 2 }}
                />
              </Source>
            )}
            {parsedCenter && (
              <Marker longitude={parsedCenter.lon} latitude={parsedCenter.lat} anchor="center">
                <div className={styles.markerDot} title="Ad reference lat/lon" />
              </Marker>
            )}
          </Map>
        </div>
      )}
    </div>
  );
}

export default GeohashPreviewMap;
